import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { ExplanationSearchBar } from "./ExplanationSearchBar";
import { parseExplanation } from "@/lib/explanationFormat";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { AIBadge, AIDisclaimer } from "@/components/ui/ai-badge";
import { supabase } from "@/integrations/supabase/client";
import {
  BookOpen,
  Sparkles,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getCachedAdvancedExplanation,
  setCachedAdvancedExplanation,
  clearCachedAdvancedExplanation,
} from "@/lib/advancedExplanationCache";
import { ExplanationBody } from "./ExplanationBody";

interface EnhancedExplanationProps {
  mcqId: string;
  question: string;
  correctAnswer: string;
  originalExplanation: string;
}

type Tab = "original" | "advanced";

type AdvState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; text: string; fromCache: boolean }
  | { kind: "error"; message: string };

function ExplanationSkeleton() {
  return (
    <div className="space-y-5 animate-fade-in" aria-label="Loading advanced explanation">
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

export function EnhancedExplanation({
  mcqId,
  question,
  correctAnswer,
  originalExplanation,
}: EnhancedExplanationProps) {
  const [tab, setTab] = useState<Tab>("original");
  const [adv, setAdv] = useState<AdvState>({ kind: "idle" });
  const [originalQuery, setOriginalQuery] = useState("");
  const [advancedQuery, setAdvancedQuery] = useState("");
  const [originalMatchCount, setOriginalMatchCount] = useState(0);
  const [advancedMatchCount, setAdvancedMatchCount] = useState(0);

  // Reset queries when MCQ changes.
  useEffect(() => {
    setOriginalQuery("");
    setAdvancedQuery("");
  }, [mcqId]);

  const showOriginalSearch =
    parseExplanation(originalExplanation).totalLength >= 120;

  const fetchAdvanced = async (opts?: { force?: boolean }) => {
    setAdv({ kind: "loading" });
    try {
      const { data, error } = await supabase.functions.invoke(
        "ai-enhance-explanation",
        {
          body: {
            originalExplanation,
            question,
            correctAnswer,
            mode: "advanced",
            generateVisual: false,
            findRelatedConcepts: false,
          },
        }
      );

      if (error) throw error;
      const text: string | undefined = data?.enhancedExplanation;
      if (!text) throw new Error("No advanced explanation returned");

      if (mcqId) setCachedAdvancedExplanation(mcqId, text);
      setAdv({ kind: "ready", text, fromCache: false });
    } catch (err) {
      console.error("Error loading advanced explanation:", err);
      setAdv({
        kind: "error",
        message:
          "We couldn't generate the advanced explanation right now. Please try again.",
      });
    }
  };

  const handleAdvancedTab = () => {
    setTab("advanced");
    if (adv.kind === "ready" || adv.kind === "loading") return;

    // Try cache first
    const cached = mcqId ? getCachedAdvancedExplanation(mcqId) : null;
    if (cached) {
      setAdv({ kind: "ready", text: cached, fromCache: true });
      return;
    }
    fetchAdvanced();
  };

  const handleRegenerate = () => {
    if (mcqId) clearCachedAdvancedExplanation(mcqId);
    fetchAdvanced({ force: true });
  };

  return (
    <Card className="p-6 space-y-4">
      {/* Two-tab switcher */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={tab === "original" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("original")}
          className={cn("flex items-center gap-2")}
        >
          <BookOpen className="h-4 w-4" />
          Original
        </Button>
        <Button
          variant={tab === "advanced" ? "default" : "outline"}
          size="sm"
          onClick={handleAdvancedTab}
          disabled={adv.kind === "loading"}
          className="flex items-center gap-2"
        >
          <Sparkles className="h-4 w-4" />
          Advanced
        </Button>

        {tab === "advanced" && adv.kind === "ready" && (
          <div className="ml-auto flex items-center gap-2">
            <AIBadge />
            {adv.fromCache && (
              <span className="text-xs text-muted-foreground">Cached</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegenerate}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
              aria-label="Regenerate advanced explanation"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate
            </Button>
          </div>
        )}
      </div>

      <Separator />

      {/* Body */}
      {tab === "original" && (
        <div className="space-y-3">
          {showOriginalSearch && (
            <ExplanationSearchBar
              value={originalQuery}
              onChange={setOriginalQuery}
              matchCount={originalMatchCount}
            />
          )}
          <ExplanationBody
            text={originalExplanation}
            mcqId={mcqId}
            source="original"
            searchQuery={originalQuery}
            onMatchCountChange={setOriginalMatchCount}
          />
        </div>
      )}

      {tab === "advanced" && (
        <>
          {adv.kind === "loading" && <ExplanationSkeleton />}

          {adv.kind === "ready" && (
            <div className="animate-fade-in space-y-3">
              {parseExplanation(adv.text).totalLength >= 120 && (
                <ExplanationSearchBar
                  value={advancedQuery}
                  onChange={setAdvancedQuery}
                  matchCount={advancedMatchCount}
                />
              )}
              <ExplanationBody
                text={adv.text}
                mcqId={mcqId}
                source="advanced"
                searchQuery={advancedQuery}
                onMatchCountChange={setAdvancedMatchCount}
              />
              <AIDisclaimer className="mt-2" />
            </div>
          )}

          {adv.kind === "error" && (
            <div
              role="alert"
              className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">{adv.message}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchAdvanced({ force: true })}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
            </div>
          )}

          {adv.kind === "idle" && (
            <p className="text-muted-foreground italic">
              Advanced explanation will appear here.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
